import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import UndoIcon from "@mui/icons-material/Undo";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import React from "react";

export type ApiErrorDialogContent = {
  title: string;
  /** Which action failed, in the user's words. */
  summary: string;
  /** One entry per problem reported by the API. */
  messages: string[];
  status?: number;
  /** Offer discarding the unsaved edits that caused the rejected request. */
  allowUndo?: boolean;
};

type ApiErrorDialogProps = {
  content: ApiErrorDialogContent | null;
  onClose: () => void;
  /** Shown only together with `allowUndo`; the dialog closes itself afterwards. */
  onUndo?: () => void;
};

/** Blocking error dialog: the user has to acknowledge it, so long API messages cannot be missed. */
const ApiErrorDialog: React.FC<ApiErrorDialogProps> = ({ content, onClose, onUndo }) => {
  const undoOffered = Boolean(content?.allowUndo && onUndo);

  return (
    <Dialog
      open={content !== null}
      onClose={(_, reason) => {
        if (reason !== "backdropClick") onClose();
      }}
      maxWidth="sm"
      fullWidth
      aria-labelledby="api-error-dialog-title"
    >
      <DialogTitle id="api-error-dialog-title">
        <Stack direction="row" alignItems="center" spacing={1}>
          <ErrorOutlineIcon color="error" />
          <span>{content?.title ?? "Action failed"}</span>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <Typography variant="body2">{content?.summary}</Typography>
          <Stack component="ul" spacing={0.75} sx={{ pl: 2.5, m: 0 }}>
            {(content?.messages ?? []).map((message, index) => (
              <Typography
                key={`${index}-${message}`}
                component="li"
                variant="body2"
                fontWeight={600}
                sx={{ overflowWrap: "anywhere" }}
              >
                {message}
              </Typography>
            ))}
          </Stack>
          {content?.status !== undefined && (
            <Typography variant="caption" color="text.secondary">
              Server response code {content.status}. Please include this text when asking an
              administrator for help.
            </Typography>
          )}
          {undoOffered && (
            <Typography variant="caption" color="text.secondary">
              Keep editing to correct the reported field, or discard your unsaved edits and return to
              the last saved state.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: undoOffered ? "space-between" : "flex-end", px: 3, pb: 2 }}>
        {undoOffered && (
          <Button
            onClick={() => {
              onUndo?.();
              onClose();
            }}
            color="secondary"
            startIcon={<UndoIcon />}
          >
            Undo changes
          </Button>
        )}
        <Box>
          <Button onClick={onClose} variant="contained" autoFocus>
            {undoOffered ? "Keep editing" : "Close"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ApiErrorDialog;
